import React from 'react';
import { useTranslation } from 'next-i18next';
import { cn } from '@gu-corp/gu-ui';
import { TierGemIcon } from './tier-gem-icon';

interface TierPassCardProps {
  tier: string; // 'White' | 'Bronze' | 'Silver' | 'Gold' | 'Platinum'
  lockedJoc: string;
  joinedAt: number; // unix timestamp
  address: string;
  onLeave?: () => void;
  leaveDisabled?: boolean;
  leaveLabel?: string;
  onGetJoc?: () => void;
  className?: string;
}

// Members card: each tier's background gradient is derived from that tier's
// own accent color (darkened, not blackened) so the card visibly reads as
// "a bronze card" / "a gold card" / etc., not a black card with a faint tint.
// Exported so other tier surfaces (e.g. the sidebar mini-pass) share the same colors.
export const TIER_ACCENTS: Record<string, { text: string; glow: string; bg: string }> = {
  White: {
    text: 'text-[#c9c4bf]',
    glow: '',
    bg: 'from-[#64625f] via-[#3c3b39] to-[#1e1d1c]',
  },
  Bronze: {
    text: 'text-[#d0a35f]',
    glow: 'shadow-[#c4934a]/20',
    bg: 'from-[#7d6239] via-[#4f3e24] to-[#251d11]',
  },
  Silver: {
    text: 'text-[#b7c9d6]',
    glow: 'shadow-[#9fb8c8]/20',
    bg: 'from-[#656f76] via-[#40464b] to-[#1d2022]',
  },
  Gold: {
    text: 'text-[#e2bc55]',
    glow: 'shadow-[#d4a83a]/30',
    bg: 'from-[#836d31] via-[#51441f] to-[#26200e]',
  },
  Platinum: {
    text: 'text-[#e8e2dc]',
    glow: 'shadow-[#dfd9d3]/30',
    bg: 'from-[#4a4d58] via-[#332f3d] to-[#17151c]',
  },
};

export const TierPassCard: React.FC<TierPassCardProps> = ({
  tier,
  lockedJoc,
  joinedAt,
  address,
  onLeave,
  leaveDisabled,
  leaveLabel,
  onGetJoc,
  className,
}) => {
  const { t } = useTranslation('common');
  const accent = TIER_ACCENTS[tier] || TIER_ACCENTS.White;
  const joinDate = joinedAt > 0 ? new Date(joinedAt * 1000).toLocaleDateString() : '-';
  const shortAddr = address ? `${address.slice(0, 6)}...${address.slice(-4)}` : '';

  return (
    <div
      className={cn(
        // Cap the width so the fixed aspect-ratio can't blow the card up into a
        // billboard on wide/full-width containers (keeps a sane vertical max too).
        'relative flex aspect-[1.586] w-full max-w-md flex-col overflow-hidden rounded-2xl p-4 shadow-xl ring-1 ring-white/10',
        'bg-gradient-to-br',
        accent.bg,
        accent.glow,
        className,
      )}
    >
      {/* Matte sheen */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(130%_70%_at_15%_0%,rgba(255,255,255,0.07),transparent_55%)]"
      />

      {/* Tone-on-tone watermark gem (top-right, tucked inside the corner) */}
      <TierGemIcon
        tier={tier}
        withColor={false}
        className="pointer-events-none absolute -right-12 -top-14 h-64 w-64 text-white opacity-[0.07]"
      />

      {/* Header: gem seal + label */}
      <div className="relative flex items-center gap-0.5">
        <TierGemIcon tier={tier} withColor={false} className={cn('h-8 w-8', accent.text)} />
        <span className="text-[10px] font-medium uppercase tracking-[0.26em] text-white/55">
          {t('joc_club.soulbound_pass')}
        </span>
      </div>

      {/* Tier status + member-since, bottom-aligned, sitting just above the divider */}
      <div className="relative mt-auto flex items-end justify-between gap-3">
        <div>
          <p className="text-[9px] uppercase tracking-[0.2em] text-white/35">
            {t('joc_club.tier_member_label')}
          </p>
          <p className={cn('text-2xl font-semibold leading-none tracking-wide', accent.text)}>
            {tier}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[9px] uppercase tracking-[0.2em] text-white/35">
            {t('joc_club.member_since')}
          </p>
          <p className="text-sm font-medium leading-none text-white/85">{joinDate}</p>
        </div>
      </div>

      <div className="relative mb-2 mt-3 h-px bg-white/10" />

      <div className="relative">
        <div className="grid grid-cols-2 gap-2 text-[10px]">
          <div className="min-w-0">
            <p className="text-white/40">{t('joc_club.locked_joc')}</p>
            <p className={cn('truncate font-medium', accent.text)}>{lockedJoc} JOC</p>
          </div>
          <div className="min-w-0">
            <p className="text-white/40">{t('joc_club.holder')}</p>
            <p className="truncate font-mono font-medium text-white/85">{shortAddr}</p>
          </div>
        </div>

        {/* Footer: non-transferable + leave/get-JOC actions */}
        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <div className="h-1.5 w-1.5 rounded-full bg-emerald-400/80" />
            <span className="text-[8px] uppercase tracking-[0.16em] text-white/35">
              {t('joc_club.non_transferable')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {onLeave && (
              <button
                onClick={onLeave}
                disabled={leaveDisabled}
                className="rounded-md border border-white/15 px-2.5 py-0.5 text-[10px] text-white/60 transition-colors hover:border-red-400/60 hover:text-red-300 disabled:opacity-40"
              >
                {leaveLabel || t('joc_club.leave')}
              </button>
            )}
            {onGetJoc && (
              <button
                onClick={onGetJoc}
                className="rounded-md border border-white/15 px-2.5 py-0.5 text-[10px] text-white/60 transition-colors hover:border-primary/60 hover:text-primary"
              >
                {t('joc_club.get_joc')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TierPassCard;
